import { Field, FieldProps, Formik, FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { object as objectYup, string as stringYup, number as numYup, boolean as booleanYup } from 'yup';
import getValue from 'lodash/get';
import { Link, useNavigate } from 'react-router-dom';
import { CandidateFormData, CandidateRequestType, SocialEnum } from '../../../types/auth';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { authService } from '../../../lib/api/auth';
import { deleteReduxProfile } from '../../../redux/registration';
import { goToRoom } from './../utils';
import { routes } from '../../routes/routes-names';
import { HighestEducationEnum } from '../../../types/auth';
import { arrayFromEnumHighestEducation } from '../../../lib/utils/emun';
import { ExperienceLevelEnum } from '../../../lib/constants/constants';
import { getHighestEducation } from '../../../lib/utils/dictionary';
import ReCAPTCHA from 'react-google-recaptcha';
import { getSiteKey, isConsentFeatureEnabled } from '../../../lib/utils/common-functions';
import websitelogo from '../../../assets/img/offerx-logo.svg';
import { getConsentWorkflowSettings } from '../../../redux/consentWorkFlow';
import { useConsentFlow, useUserProfile } from '../../../hooks/useSelector';
import {
    sessionStorageClearAll,
    sessionStorageGetItem,
    sessionStorageRemoveItem,
    sessionStorageSetItem,
} from '../../../components/storage/sessionStorageManager';
import { localStorageGetItem, localStorageRemoveItem } from '../../../components/storage/localStorageManager';
import { useAppDispatch } from '../../../redux/reduxhooks';
import { isCaptchaEnabled } from '../../../config/constants';

type Props = {
    candidateId?: string;
    handleSelect: (key: string, key2: string) => void;
};

const initialFormData = {
    JobTitle: '',
    HighestEducation: '',
    ExperienceLevel: '',
    PrevCompany: '',
    IsCandidateTermsChecked: false,
    recaptcha: '',
    InstitutionName: '',
    GraduationYear: '',
    UserCountryCode: '',
};

const CandidateStep3: React.FC<Props> = ({ handleSelect }) => {
    const dispatch = useAppDispatch();
    const consentWorkFlowSettings = useConsentFlow();
    const reduxProfile = useUserProfile();
    const [sessionDataStep2, setSessionDataStep2] = useState<any>();
    const [selectedDate, setSelectedDate] = useState<any>();
    const [sessionDataStep3, setSessionDataStep3] = useState<any>();
    const [experienceLevelValue, SetExperienceLevelValue] = useState('');
    const [candidateId, setCandidateId] = useState<string | null>('');
    const [selectedCountry, setSelectedCountry] = useState<string>('101');
    const [passString, setPassString] = useState('');

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    const navigate = useNavigate();

    const now = new Date().getUTCFullYear();
    const graduationYears = Array(now - (now - 66))
        .fill('')
        .map((v, idx) => now - idx);

    useEffect(() => {
        if (!consentWorkFlowSettings?.settings) dispatch(getConsentWorkflowSettings());

        let data = sessionStorageGetItem('candidate-basic-details', false)!;
        const data2 = sessionStorageGetItem('candidateStep3', false)!;
        const storedDateStr = sessionStorageGetItem('selectedDate');
        const storedPhoneCode = sessionStorageGetItem('phoneCode', false)!;

        delete data2?.recaptcha;

        setSelectedCountry(storedPhoneCode);
        setSessionDataStep2(data);
        setSessionDataStep3(data2);
        setSelectedDate(storedDateStr);
        SetExperienceLevelValue(data2?.ExperienceLevel === 1 ? 'Experienced' : 'Fresher');

        const inviteId = sessionStorageGetItem('invitationId', false)!;
        setCandidateId(inviteId);

        window.onbeforeunload = () => {
            sessionStorageRemoveItem('candidate-basic-details');
            sessionStorageRemoveItem('candidateStep3');
            sessionStorageRemoveItem('selectedDate');
        };
    }, []);

    const sessionFormDataStep2 = useMemo(
        () =>
            sessionDataStep2
                ? {
                      ...initialFormData,
                      FirstName: sessionDataStep2?.FirstName,
                      MiddleName: sessionDataStep2?.MiddleName,
                      LastName: sessionDataStep2?.LastName,
                      Email: sessionDataStep2?.Email,
                      CityDistrict: sessionDataStep2?.CityDistrict,
                      state: sessionDataStep2?.state,
                      country: sessionDataStep2?.country,
                      Dob: selectedDate,
                      flatAddress: sessionDataStep2?.flatAddress,
                      streetAddress: sessionDataStep2?.streetAddress,
                      PostalCode: sessionDataStep2?.PostalCode,
                      Gender: +sessionDataStep2?.Gender,
                      Phone: sessionDataStep2?.Phone,
                      //   Aadhar: sessionDataStep2?.Aadhar,
                      GraduationYear: sessionDataStep2?.GraduationYear,
                      InstitutionName: sessionDataStep2?.InstitutionName,
                      UserCountryCode: sessionDataStep2?.UserCountryCode,
                  }
                : initialFormData,
        [sessionDataStep2, selectedDate]
    );

    const sessionFormDataStep3 = useMemo(
        () =>
            sessionDataStep3
                ? {
                      ...sessionDataStep2,
                      HighestEducation:
                          sessionDataStep3?.HighestEducation === '' ? '' : Number(sessionDataStep3?.HighestEducation),
                      ExperienceLevel:
                          sessionDataStep3?.ExperienceLevel === '' ? '' : Number(sessionDataStep3?.ExperienceLevel),
                      PrevCompany: sessionDataStep3?.PrevCompany,
                      ...(candidateId && { InvitationId: candidateId }),
                      JobTitle: sessionDataStep3?.JobTitle,
                      ...(candidateId && { InvitationId: candidateId }),
                      IsCandidateTermsChecked: sessionDataStep3?.IsCandidateTermsChecked,
                      recaptcha: sessionDataStep3?.recaptcha,
                      GraduationYear: sessionDataStep3?.GraduationYear,
                      InstitutionName: sessionDataStep3?.InstitutionName,
                  }
                : initialFormData,
        [sessionDataStep3, sessionDataStep2, candidateId]
    );

    const invalidYearAlert = 'Founded year should be between 1900 and ' + new Date().getFullYear();

    const getValidationSchema = (captchaEnabled: boolean) => {
        if (!isConsentFeatureEnabled(consentWorkFlowSettings)) {
            return objectYup().shape({
                HighestEducation: numYup().required('Highest education is required.'),
                ExperienceLevel: numYup().required('Work experience is required.'),
                // JobTitle: stringYup().when('ExperienceLevel', {
                //     is: ExperienceLevelEnum.Experienced,
                //     then: stringYup().required('Recent job title is required.'),
                // }),
                JobTitle: stringYup().when('ExperienceLevel', ([ExperienceLevel], schema) => {
                    return ExperienceLevel === ExperienceLevelEnum.Experienced
                        ? schema.required('Job title is required.')
                        : schema;
                }),
                PrevCompany: stringYup().when('experienceLevel', ([experienceLevel], schema) => {
                    return experienceLevel === ExperienceLevelEnum.Experienced
                        ? schema.required('Recent company is required.')
                        : schema;
                }),
                // PrevCompany: stringYup().when('ExperienceLevel', {
                //     is: ExperienceLevelEnum.Experienced,
                //     then: stringYup().required('Recent company is required.'),
                // }),
                IsCandidateTermsChecked: booleanYup().oneOf([true], 'Please accept terms and conditions to register.'),
                recaptcha: captchaEnabled
                    ? stringYup().nullable().required('Captcha is required.')
                    : stringYup().nullable(),
                InstitutionName: stringYup().required('Institution is required.'),
                GraduationYear: numYup()
                    .nullable()
                    .integer()
                    .label('Graduation Year')
                    .when('Graduation Year', (foundedYear, schema) => {
                        if (foundedYear) {
                            return schema.min(1900, invalidYearAlert).max(new Date().getFullYear(), invalidYearAlert);
                        }
                        return schema;
                    })
                    .required('Graduation year is required.'),
            });
        } else {
            return objectYup().shape({
                HighestEducation: numYup().required('Highest education is required.'),
                ExperienceLevel: numYup().required('Work experience is required.'),
                // JobTitle: stringYup().when('ExperienceLevel', {
                //     is: ExperienceLevelEnum.Experienced,
                //     then: stringYup().required('Recent job title is required.'),
                // }),
                JobTitle: stringYup().when('ExperienceLevel', ([ExperienceLevel], schema) => {
                    return ExperienceLevel === ExperienceLevelEnum.Experienced
                        ? schema.required('Job title is required.')
                        : schema;
                }),
                PrevCompany: stringYup().when('experienceLevel', ([experienceLevel], schema) => {
                    return experienceLevel === ExperienceLevelEnum.Experienced
                        ? schema.required('Recent company is required.')
                        : schema;
                }),
                // PrevCompany: stringYup().when('ExperienceLevel', {
                //     is: ExperienceLevelEnum.Experienced,
                //     then: stringYup().required('Recent company is required.'),
                // }),
                // IsCandidateTermsChecked: booleanYup().oneOf([true], 'Please accept terms and conditions to register.'),
                recaptcha: captchaEnabled
                    ? stringYup().nullable().required('Captcha is required.')
                    : stringYup().nullable(),
                InstitutionName: stringYup().required('Institution is required.'),
                GraduationYear: numYup()
                    .nullable()
                    .integer()
                    .label('Graduation Year')
                    .when('Graduation Year', (foundedYear, schema) => {
                        if (foundedYear) {
                            return schema.min(1900, invalidYearAlert).max(new Date().getFullYear(), invalidYearAlert);
                        }
                        return schema;
                    })
                    .required('Graduation year is required.'),
            });
        }
    };

    const initialErrors = useInitialErrors(sessionFormDataStep2, getValidationSchema(captchaEnabled));

    const submitForm = useCallback(
        async (values: CandidateFormData) => {
            // if (!reduxProfile) return;

            const candidateAddressValue =
                sessionDataStep2?.flatAddress +
                '%' +
                sessionDataStep2?.streetAddress +
                '%' +
                sessionDataStep2?.PostalCode;

            const requestData: CandidateRequestType = {
                ...values,
                // Aadhar: values.Aadhar.slice(-4),
                Phone: values.Phone,
                CityDistrict: values.CityDistrict === '' ? null : values.CityDistrict,
                state: values.state === '' ? null : values.state,
                Gender: Number(values.Gender),
                CandidateAddress: candidateAddressValue,
                Dob: values.Dob as Date,
                AuthType: passString ? SocialEnum.CustomAuth : reduxProfile?.authType!,
                Password: (passString && passString) || '',
                Token: reduxProfile?.token! || '',
                HighestEducation: values.HighestEducation === '' ? null : Number(values.HighestEducation),
                ExperienceLevel: values.ExperienceLevel === '' ? null : Number(values.ExperienceLevel),
                PrevCompany: values.PrevCompany,
                ...(candidateId && { InvitationId: candidateId }),
            };

            try {
                const data = await authService.candidateSignUp(requestData);
                if (data) {
                    dispatch(deleteReduxProfile());
                    sessionStorageClearAll();
                    localStorageRemoveItem('Password');
                    localStorageRemoveItem('Email');
                    await goToRoom(data, dispatch, navigate);
                }
            } catch (err) {}
        },
        [
            reduxProfile,
            sessionDataStep2?.flatAddress,
            sessionDataStep2?.streetAddress,
            sessionDataStep2?.PostalCode,
            passString,
            candidateId,
            dispatch,
        ]
    );
    useEffect(() => {
        const CustomAuthPass = localStorageGetItem('Password')!;

        if (CustomAuthPass) {
            setPassString(CustomAuthPass);
        }
    }, []);

    return (
        <>
            <Formik
                onSubmit={submitForm}
                enableReinitialize
                initialValues={sessionDataStep3 !== null ? sessionFormDataStep3 : sessionFormDataStep2}
                validationSchema={getValidationSchema(captchaEnabled)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<CandidateFormData>) => {
                    const { handleSubmit, values, isSubmitting, setFieldTouched, setFieldValue } = formikProps;
                    const siteKey = getSiteKey();

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="card lt-shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="container-fluid">
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <img
                                                    loading="lazy"
                                                    src={websitelogo}
                                                    className="mx-auto d-block mb-2"
                                                    width={120}
                                                    alt="Logo"
                                                />
                                                <div className="fs-18 fw-600 text-center">Sign Up</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="fs-18 fw-600 mb-3">Education</div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Highest Education <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="HighestEducation">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <select
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                        }}
                                                                        className={`form-select cursor-pointer ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                    >
                                                                        <option value="">
                                                                            Select Your Highest Education
                                                                        </option>
                                                                        {arrayFromEnumHighestEducation(
                                                                            HighestEducationEnum
                                                                        ).map((item) => (
                                                                            <option key={item} value={item}>
                                                                                {getHighestEducation(item)}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Institution Name <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="InstitutionName">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        maxLength={160}
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            const regex = /^[a-zA-Z0-9 ]*$/;

                                                                            if (regex.test(ev.target.value)) {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Institution Name as on your Highest Educational Certificate"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Graduation Year{' '}
                                                        <i className="bi bi-info-circle lt-tooltip">
                                                            <span className="lt-tooltiptext lt-shadow-md fw-400 fs-14 bottom-tip">
                                                                Select Graduation Year as on your Educational
                                                                Certificate.
                                                            </span>
                                                        </i>{' '}
                                                        <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="GraduationYear">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <select
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                        }}
                                                                        className={`form-select cursor-pointer ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                    >
                                                                        <option value="">Select</option>
                                                                        {graduationYears.map((item) => (
                                                                            <option key={item} value={item}>
                                                                                {item}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-md-12">
                                                <div className="fs-18 fw-600 mb-3">Experience</div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Work Experience <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="ExperienceLevel">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field } = fieldProps;
                                                            return (
                                                                <>
                                                                    <div className="form-check form-check-inline mb-0">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="inlineRadioOptions"
                                                                            id="FresherRadio"
                                                                            checked={
                                                                                field.value ===
                                                                                ExperienceLevelEnum.Fresher
                                                                            }
                                                                            value={field.value}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.name);

                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ExperienceLevelEnum.Fresher
                                                                                );
                                                                                SetExperienceLevelValue(
                                                                                    ExperienceLevelEnum[
                                                                                        ExperienceLevelEnum.Fresher
                                                                                    ]
                                                                                );
                                                                            }}
                                                                        />
                                                                        <label
                                                                            className="form-check-label fs-14"
                                                                            htmlFor="FresherRadio"
                                                                        >
                                                                            Fresher
                                                                        </label>
                                                                    </div>
                                                                    <div className="form-check form-check-inline mb-0">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="inlineRadioOptions"
                                                                            id="ExperiencedRadio"
                                                                            checked={
                                                                                field.value ===
                                                                                ExperienceLevelEnum.Experienced
                                                                            }
                                                                            value={field.value}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ExperienceLevelEnum.Experienced
                                                                                );
                                                                                SetExperienceLevelValue(
                                                                                    ExperienceLevelEnum[
                                                                                        ExperienceLevelEnum.Experienced
                                                                                    ]
                                                                                );
                                                                            }}
                                                                        />
                                                                        <label
                                                                            className="form-check-label fs-14"
                                                                            htmlFor="ExperiencedRadio"
                                                                        >
                                                                            Experienced
                                                                        </label>
                                                                    </div>
                                                                    <Field name="ExperienceLevel">
                                                                        {(fieldProps: FieldProps) => {
                                                                            const { field, form } = fieldProps;
                                                                            const error =
                                                                                getValue(form.touched, field.name) &&
                                                                                getValue(form.errors, field.name);
                                                                            return (
                                                                                <>
                                                                                    <div className="d-flex justify-content-between">
                                                                                        {typeof error === 'string' && (
                                                                                            <small className="text-danger mt-2 fs-12">
                                                                                                {error}
                                                                                            </small>
                                                                                        )}
                                                                                    </div>
                                                                                </>
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            {experienceLevelValue ===
                                                ExperienceLevelEnum[ExperienceLevelEnum.Experienced] && (
                                                <>
                                                    <div className="col-md-12">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2">
                                                                Recent Job Title{' '}
                                                                <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="JobTitle">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <>
                                                                            <input
                                                                                type="text"
                                                                                maxLength={100}
                                                                                value={field.value}
                                                                                onChange={(ev) => {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                }}
                                                                                className={`form-control ${
                                                                                    error ? 'is-invalid' : ''
                                                                                }`}
                                                                                placeholder="Enter Your Job Title"
                                                                            />
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2">
                                                                Recent Company <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="PrevCompany">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <>
                                                                            <input
                                                                                type="text"
                                                                                maxLength={100}
                                                                                value={field.value}
                                                                                onChange={(ev) => {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                }}
                                                                                className={`form-control ${
                                                                                    error ? 'is-invalid' : ''
                                                                                }`}
                                                                                placeholder="Enter Your Recent Company"
                                                                            />
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {!isConsentFeatureEnabled(consentWorkFlowSettings) && (
                                                <div className="col-md-12">
                                                    <div className="mb-3">
                                                        <div className="form-check mb-2 custom-h-40">
                                                            <Field
                                                                className="form-check-input cursor-pointer"
                                                                name="IsCandidateTermsChecked"
                                                                type="checkbox"
                                                                id="check"
                                                                checked={values.IsCandidateTermsChecked}
                                                            ></Field>
                                                            <label
                                                                className="form-check-label fs-13 mt-1 cursor-pointer lt-text-secondary"
                                                                htmlFor="check"
                                                            >
                                                                I have read and agree to the{' '}
                                                                <Link to={routes.privacy} target="_blank">
                                                                    Privacy Policy
                                                                </Link>{' '}
                                                                and{' '}
                                                                <Link to={routes.terms} target="_blank">
                                                                    Terms & Conditions
                                                                </Link>
                                                                .
                                                            </label>
                                                            <Field name="IsCandidateTermsChecked">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <div>
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {captchaEnabled && (
                                                <div className="col-md-12">
                                                    <div className="mb-4">
                                                        <Field name="recaptcha">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <div>
                                                                        <ReCAPTCHA
                                                                            sitekey={siteKey}
                                                                            onChange={(value) => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(field.name, value);
                                                                            }}
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="my-3 d-flex justify-content-between">
                                        <button
                                            onClick={() => {
                                                handleSelect('basic-details', 'candidate');
                                                sessionStorageSetItem('candidateStep3', values, false);
                                            }}
                                            className="btn btn-outline-primary outline-p-hover reg-next-button me-3"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn btn-primary reg-next-button"
                                        >
                                            {isSubmitting && <span className="spinner-border spinner-border-sm" />}Join
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </>
    );
};

export default CandidateStep3;
